// Support page — publicly accessible (no login required).
// Required for App Store / Google Play / Microsoft Store submission.
// Provides contact info, FAQs, subscription help, refund policy, and links.

import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, LifeBuoy, MessageSquare, CreditCard, ShieldCheck, ArrowLeft, ExternalLink } from 'lucide-react';
import Disclaimer from '../components/Disclaimer';
import SelahBrandMark from '../components/SelahBrandMark';
import { isIOS } from '../utils/platform';

const SUPPORT_EMAIL = 'support@selahdownloads.com';
const COMPANY_URL = 'https://selahandstonelegacy.com';

// ─────────────────────────────────────────────────────────────────────────
// FAQ IS PLATFORM-AWARE (Apple App Review Guideline 3.1.1 — Anti-Steering)
//
// iOS builds MUST NOT display external subscription pricing, steer users to
// an external purchase/manage flow, or explain why the app avoids Apple's
// IAP system. Non-iOS builds (web, Android, Windows) continue to disclose
// pricing and Stripe billing details — this is required for those stores'
// transparency policies.
// ─────────────────────────────────────────────────────────────────────────
function buildFAQ(iosMode) {
  const faq = [
    // ══════════════════════════════════════════════════════════════════
    //  TRIAL & PRICING
    // ══════════════════════════════════════════════════════════════════
    {
      q: 'How does the 7-day free trial work?',
      a: "When you sign up for Selah Downloads you get 7 days of full Premium access — no credit card required upfront. Your trial starts the moment you create your account. On Day 8 the trial ends automatically and nothing is charged.",
    },
  ];

  // Pricing disclosure — platform-aware.
  //   - iOS   : $7.79 (closest Apple tier to our covenant $7.77) and no 7·7·7
  //             theology, because Apple's price ladder cannot express $7.77
  //             exactly. Keeping the theology visible next to a "$7.79"
  //             price would be inconsistent.
  //   - Other : $7.77 with the full covenant of completeness explanation.
  faq.push({
    q: 'How much does Selah Downloads cost?',
    a: iosMode
      ? "Selah Downloads Premium is $7.79 USD per month on the App Store. There are no hidden fees, no setup charges, and no long-term commitment. Cancel anytime."
      : "Selah Downloads Premium is $7.77 USD per month — a covenant of completeness, order, and rest. There are no hidden fees, no setup charges, and no long-term commitment. Cancel anytime.",
  });

  // Web-only FAQ that transparently explains the 2¢ price difference to
  // curious users who compare pricing across platforms. This entry is
  // intentionally NOT shown on iOS — Apple's App Review Guideline 3.1.1
  // forbids mentioning external prices or steering users away from the
  // App Store from within an iOS build.
  if (!iosMode) {
    faq.push({
      q: 'Why is the iPhone price $7.79 instead of $7.77?',
      a: "Apple's App Store price ladder increments in 10-cent steps under $10, so the closest available price to our covenant $7.77 is $7.79. The two-cent difference on iPhone/iPad offsets Apple's platform fee. On the web, on Android via Google Play, and on Windows via the Microsoft Store, the covenant $7.77 price is preserved. The 7·7·7 theology — three sevens for divine completeness, order, and rest — remains true to its original meaning wherever it appears.",
    });
  }

  faq.push({
    q: 'What happens when my 7-day trial ends?',
    a: iosMode
      ? "Nothing dramatic. If you subscribed via the App Store during your trial, your first monthly charge is processed by Apple and Premium continues seamlessly. If you didn't subscribe, your account is preserved (nothing is deleted) but Premium features pause. Your encrypted downloads are always safe."
      : "Nothing dramatic. If you subscribed during the trial, your first monthly charge processes and you continue with uninterrupted Premium access. If you didn't subscribe, your account is preserved (nothing is deleted) but Premium features pause until you subscribe. You can subscribe at any time and pick up right where you left off — your encrypted downloads are always safe.",
  });

  // ══════════════════════════════════════════════════════════════════
  //  BILLING & CHARGES
  //  Platform-aware: iOS uses Apple in-app billing, others use Stripe.
  // ══════════════════════════════════════════════════════════════════
  if (iosMode) {
    faq.push({
      q: 'How does billing work on iPhone / iPad?',
      a: (
        <>
          Selah Downloads on iOS uses Apple&apos;s official In-App Purchase system.
          When you subscribe, the charge appears on your Apple ID payment method
          (whichever card, PayPal, or Apple ID balance you have on file with
          Apple). All billing is handled by Apple — Selah Downloads never sees
          your payment details.
          <br /><br />
          Your subscription is tied to your Apple ID, so you can restore it on
          any of your Apple devices using the same Apple ID by tapping{' '}
          <strong>Restore Purchases</strong> in the app.
        </>
      ),
    });
    faq.push({
      q: 'How will the charge appear on my statement?',
      a: (
        <>
          Because iOS subscriptions are billed by Apple, the charge appears as{' '}
          <strong>APPLE.COM/BILL</strong> or <strong>iTunes.com/Bill</strong>{' '}
          on your bank or credit card statement — not as &quot;Selah Downloads&quot;.
          <br /><br />
          You can review every Apple charge (and see that it was for Selah
          Downloads) at{' '}
          <a
            href="https://reportaproblem.apple.com"
            target="_blank"
            rel="noreferrer"
            className="text-[var(--sd-primary-dark)] hover:underline"
          >
            reportaproblem.apple.com
          </a>
          .
        </>
      ),
    });
  } else {
    faq.push({
      q: 'How will Selah Downloads appear on my credit card statement?',
      a: (
        <>
          Your monthly charge will appear as one of the following on your bank or credit card statement:
          <ul style={{ margin: '10px 0 10px 20px', listStyle: 'disc' }}>
            <li><strong>SELAH DOWNLOADS</strong></li>
            <li><strong>SELAH*DOWNLOADS</strong></li>
            <li><strong>SELAH &amp; STONE LEGACY HOLDINGS LLC</strong> (our parent company)</li>
          </ul>
          All three represent the same charge for your Selah Downloads Premium subscription. If you see an unfamiliar charge, please email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--sd-primary-dark)] hover:underline">support@selahdownloads.com</a> BEFORE disputing with your bank — we can quickly confirm and resolve any confusion.
        </>
      ),
    });
    faq.push({
      q: 'What payment methods do you accept?',
      a: "Selah Downloads accepts all major credit and debit cards (Visa, Mastercard, American Express, Discover), Apple Pay, Google Pay, and Link (Stripe's one-click checkout). Payments are processed by Stripe on their secure servers — Selah Downloads never sees or stores your card details.",
    });
  }

  faq.push({
    q: 'Can I use my Selah Downloads subscription on multiple devices?',
    a: "Yes. Your subscription is tied to your account, not to a specific device. Sign in with the same email and password on any device and you'll have full Premium access. There is no device limit and no separate purchase required per platform.",
  });

  // ══════════════════════════════════════════════════════════════════
  //  CANCELLATION & REFUNDS
  // ══════════════════════════════════════════════════════════════════
  if (iosMode) {
    faq.push({
      q: 'How do I manage or cancel my subscription?',
      a: (
        <>
          Because your iOS subscription is billed by Apple, cancellation happens
          in Apple&apos;s Subscription Manager (not inside Selah Downloads):
          <br /><br />
          <strong>On your iPhone / iPad:</strong>{' '}
          Settings → your name → Subscriptions → Selah Downloads → Cancel Subscription.
          <br />
          <strong>On a Mac:</strong>{' '}
          App Store → click your profile picture (bottom-left) → View Information →
          scroll to Subscriptions → Manage → Selah Downloads → Cancel.
          <br /><br />
          Access continues until the end of your current billing period.
        </>
      ),
    });
  } else {
    faq.push({
      q: 'How do I cancel my Selah Downloads subscription?',
      a: (
        <>
          Open the Selah Downloads app → <strong>Settings → Subscription → Manage subscription</strong>. This opens your secure Stripe billing portal where you can cancel with one tap. Your access continues until the end of the current billing period (no proration, no cancellation fees). You can also visit <a href="https://selahdownloads.com/settings" className="text-[var(--sd-primary-dark)] hover:underline">selahdownloads.com/settings</a> from any browser.
        </>
      ),
    });
  }

  faq.push({
    q: 'Can I get a refund?',
    a: iosMode ? (
      <>
        Refunds for App Store purchases are handled by Apple. Visit{' '}
        <a
          href="https://reportaproblem.apple.com"
          target="_blank"
          rel="noreferrer"
          className="text-[var(--sd-primary-dark)] hover:underline"
        >
          reportaproblem.apple.com
        </a>{' '}
        within 90 days of the charge, find the Selah Downloads transaction, and
        request a refund. Apple reviews each request individually. If you also
        have questions specific to your Selah account, email{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--sd-primary-dark)] hover:underline">
          support@selahdownloads.com
        </a>{' '}
        and we&apos;ll help however we can.
      </>
    ) : (
      <>
        For refunds, please contact us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--sd-primary-dark)] hover:underline">support@selahdownloads.com</a> within 14 days of purchase and include your account email. We review each request individually and honor genuine cases — no need to dispute with your bank first.
      </>
    ),
  });

  // ══════════════════════════════════════════════════════════════════
  //  PRIVACY, DATA & ACCOUNT
  // ══════════════════════════════════════════════════════════════════
  faq.push({
    q: 'How is my data protected?',
    a: iosMode
      ? "Every download you save to your journey is encrypted on your device with a key derived from your password (PBKDF2). Selah Downloads never sees the plaintext of your spiritual downloads. Ambient captures (voice shortcuts / webhooks) are stored in plaintext until you review them and 'Save to Journey' — at that point they're encrypted just like the others."
      : "Every download you save to your journey is encrypted on your device with a key derived from your password (PBKDF2). Selah Downloads never sees the plaintext of your spiritual downloads. Ambient captures (voice shortcuts / webhooks) are stored in plaintext until you review them and 'Save to Journey' — at that point they're encrypted just like the others. Payment info is handled by Stripe (PCI-DSS Level 1 certified); we never store card numbers.",
  });
  faq.push({
    q: 'What if I forget my password?',
    a: "Because your saved downloads are encrypted with a key derived from your password, we cannot reset it and still restore your data. Please reach out to support and we can help you regain access to your account (with a fresh encryption key for future entries). To be safe, keep a copy of your password in a password manager.",
  });
  faq.push({
    q: 'Can I delete my Selah Downloads account?',
    a: (
      <>
        Yes. From inside the app go to <strong>Settings → Danger Zone → Delete Account</strong>.
        You can also delete without signing in via the{' '}
        <Link to="/delete-account" className="text-[var(--sd-primary-dark)] hover:underline">public delete form</Link>.
        Deletion removes your account, all encrypted downloads, and ambient captures immediately and permanently.
      </>
    ),
  });

  // ══════════════════════════════════════════════════════════════════
  //  PLATFORMS & AVAILABILITY
  // ══════════════════════════════════════════════════════════════════
  // On iOS this FAQ MUST NOT mention competing platforms (Google Play,
  // Microsoft Store) or steer users to a Progressive Web App install.
  // Both are textbook Guideline 3.1.1 anti-steering violations — see
  // Apple's Aug 21 2026 rejection which cited the reviewer opening this
  // exact FAQ on an iPad Air (M3). We instead answer only in terms of
  // Apple devices (iPhone / iPad / Mac with Apple Silicon).
  faq.push({
    q: 'Which devices is Selah Downloads available on?',
    a: iosMode
      ? "Selah Downloads runs on iPhone and iPad. Your account and encrypted downloads sync across every Apple device where you're signed in."
      : "Selah Downloads works on every modern browser (Chrome, Edge, Safari, Firefox), and you can install it as a Progressive Web App on Windows, macOS, ChromeOS, iOS, and Android. Store-distributed versions are available on the Apple App Store, Google Play Store, and Microsoft Store. All versions share the same account.",
  });

  return faq;
}

const Support = () => {
  // Build the FAQ per-render so the iOS platform check evaluates fresh
  // every time (after localStorage-based iOS memory has hydrated, after
  // React Router mounts, after the Swift bridge has registered its
  // handlers). Building at module load — the previous behaviour — meant
  // one unlucky race with the URL query param would show Apple's App
  // Review reviewer the web FAQ, which mentions Stripe, $7.77 pricing,
  // and a price comparison between iOS and other platforms. Any of those
  // would trigger an immediate 3.1.1 anti-steering re-rejection.
  const FAQ = buildFAQ(isIOS());
  return (
    <div className="min-h-screen flex flex-col bg-[var(--sd-bg)]">
      <div className="flex-1 hero-gradient">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)] mb-6"
            data-testid="support-back-link"
          >
            <ArrowLeft size={14} />
            Back to Selah Downloads
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <SelahBrandMark size={44} sparkleSize={13} />
            <div>
              <p className="text-xs uppercase tracking-wider text-[var(--sd-primary-dark)] font-semibold">Support</p>
              <h1 className="text-3xl font-brand text-[var(--sd-fg)]">How can we help?</h1>
            </div>
          </div>
          <p className="text-base text-[var(--sd-fg-secondary)] mb-8 leading-relaxed">
            Selah Downloads is built by a small team. When you reach out, a human reads your note.
          </p>

          {/* Contact + quick links */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            <Card className="glass-card p-5" data-testid="support-contact-card">
              <div className="flex items-start gap-3">
                <Mail size={20} className="text-[var(--sd-primary-dark)] mt-0.5" />
                <div className="min-w-0">
                  <h3 className="font-semibold text-[var(--sd-fg)] mb-1">Email support</h3>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="text-sm text-[var(--sd-primary-dark)] hover:underline break-all"
                    data-testid="support-email-link"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                  <p className="text-xs text-[var(--sd-muted-fg)] mt-2">
                    Typical response within 1–2 business days.
                  </p>
                </div>
              </div>
            </Card>
            <Card className="glass-card p-5" data-testid="support-parent-card">
              <div className="flex items-start gap-3">
                <LifeBuoy size={20} className="text-[var(--sd-primary-dark)] mt-0.5" />
                <div>
                  <h3 className="font-semibold text-[var(--sd-fg)] mb-1">Parent company</h3>
                  <a
                    href={COMPANY_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-[var(--sd-primary-dark)] hover:underline inline-flex items-center gap-1"
                  >
                    selahandstonelegacy.com
                    <ExternalLink size={12} />
                  </a>
                  <address
                    className="not-italic text-xs text-[var(--sd-muted-fg)] mt-2 leading-relaxed"
                    data-testid="support-business-address"
                  >
                    Selah &amp; Stone Legacy Holdings LLC<br />
                    5005 W. Laurel, Ste. 100<br />
                    Tampa, FL 33607, USA
                  </address>
                </div>
              </div>
            </Card>
          </div>

          {/* FAQ */}
          <h2 className="text-xl font-semibold text-[var(--sd-fg)] mb-4 flex items-center gap-2">
            <MessageSquare size={18} className="text-[var(--sd-primary-dark)]" />
            Frequently asked questions
          </h2>
          <div className="space-y-3 mb-10">
            {FAQ.map((item, i) => (
              <Card key={i} className="glass-card p-5" data-testid={`support-faq-${i}`}>
                <h3 className="font-semibold text-[var(--sd-fg)] mb-1.5">{item.q}</h3>
                <div className="text-sm text-[var(--sd-fg-secondary)] leading-relaxed">{item.a}</div>
              </Card>
            ))}
          </div>

          {/* Legal + quick links */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
            <Link to="/privacy" className="glass-card p-4 hover:border-[var(--sd-primary-dark)] transition-colors" data-testid="support-privacy-link">
              <ShieldCheck size={18} className="text-[var(--sd-primary-dark)] mb-1" />
              <h3 className="text-sm font-semibold text-[var(--sd-fg)]">Privacy Policy</h3>
              <p className="text-xs text-[var(--sd-muted-fg)] mt-1">Data collection &amp; encryption</p>
            </Link>
            <Link to="/terms" className="glass-card p-4 hover:border-[var(--sd-primary-dark)] transition-colors" data-testid="support-terms-link">
              <CreditCard size={18} className="text-[var(--sd-primary-dark)] mb-1" />
              <h3 className="text-sm font-semibold text-[var(--sd-fg)]">Terms &amp; Conditions</h3>
              <p className="text-xs text-[var(--sd-muted-fg)] mt-1">Subscription &amp; refund policy</p>
            </Link>
            <Link to="/delete-account" className="glass-card p-4 hover:border-[var(--sd-primary-dark)] transition-colors" data-testid="support-delete-link">
              <LifeBuoy size={18} className="text-[var(--sd-primary-dark)] mb-1" />
              <h3 className="text-sm font-semibold text-[var(--sd-fg)]">Delete your account</h3>
              <p className="text-xs text-[var(--sd-muted-fg)] mt-1">Removes all data — no login required</p>
            </Link>
          </div>

          <div className="text-center pt-4">
            <Button asChild variant="outline" className="border-[var(--sd-primary-dark)]/50">
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Selah Downloads Support`}>
                <Mail size={16} className="mr-2" />
                Contact Support
              </a>
            </Button>
          </div>
        </div>
      </div>
      <Disclaimer />
    </div>
  );
};

export default Support;
